// Returns active broadcasts matching the user's audience. Provides:
//   - broadcasts: all in-window items (for the "view all" sheet / ticker loop)
//   - primary:    the most recent unread item (drives the inline banner emphasis)
//   - dismiss():  marks the primary as read
//   - dismissAll(): marks every visible item as read (used by "Got it" in the dialog)
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isAndroidApp } from "@/lib/platform";

export interface Broadcast {
  id: string;
  title: string;
  body: string;
  cta_label: string | null;
  cta_url: string | null;
  audience: string;
  starts_at: string;
  ends_at: string | null;
}

export function useActiveBroadcast() {
  const { user } = useAuth();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setBroadcasts([]);
      setReadIds(new Set());
      setLoading(false);
      return;
    }
    let active = true;

    (async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("credits_purchased_total")
        .eq("user_id", user.id)
        .maybeSingle();
      const isPaid = Number(profile?.credits_purchased_total ?? 0) > 0;
      const audiences = ["all", isPaid ? "paid" : "free"];
      if (isAndroidApp()) audiences.push("android");

      const { data: items } = await supabase
        .from("broadcasts")
        .select("id, title, body, cta_label, cta_url, audience, starts_at, ends_at")
        .in("audience", audiences)
        .order("starts_at", { ascending: false })
        .limit(20);

      if (!active) return;
      const list = items ?? [];
      if (!list.length) {
        setBroadcasts([]);
        setLoading(false);
        return;
      }

      const { data: reads } = await supabase
        .from("broadcast_reads")
        .select("broadcast_id")
        .eq("user_id", user.id)
        .in("broadcast_id", list.map((i) => i.id));

      if (!active) return;
      setBroadcasts(list);
      setReadIds(new Set((reads ?? []).map((r) => r.broadcast_id)));
      setLoading(false);
    })();

    return () => { active = false; };
  }, [user?.id]);

  const markRead = useCallback(async (ids: string[]) => {
    if (!user || !ids.length) return;
    const rows = ids.map((broadcast_id) => ({ broadcast_id, user_id: user.id }));
    await supabase.from("broadcast_reads").upsert(rows, { onConflict: "broadcast_id,user_id", ignoreDuplicates: true });
    setReadIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, [user?.id]);

  const primary = broadcasts.find((b) => !readIds.has(b.id)) ?? null;

  const dismiss = async () => {
    if (primary) await markRead([primary.id]);
  };
  const dismissAll = async () => {
    await markRead(broadcasts.map((b) => b.id));
  };

  return { broadcasts, primary, readIds, loading, dismiss, dismissAll };
}
